import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Search, RefreshCw, Package, MapPin, CheckCircle2, Truck, Pill, ShieldCheck, Clock, UserCheck, XCircle, AlertTriangle, Navigation, Check, X, Send } from 'lucide-react';
import { UnifiedOrder, OrderStatus } from '../../types';
import { mockUnifiedOrders } from '../../utils/mockData';
import { cn } from '../../utils/cn';
import { useToast } from '../../components/ui/Toast';
const REJECTION_REASONS = [{
  value: 'out_of_stock',
  label: 'Out of Stock'
}, {
  value: 'invalid_prescription',
  label: 'Invalid Prescription'
}, {
  value: 'patient_request',
  label: 'Patient Request'
}, {
  value: 'insurance_rejected',
  label: 'Insurance Rejected'
}, {
  value: 'other',
  label: 'Other'
}];
const refillColumns = [{
  id: 'pending',
  label: 'Pending',
  statuses: ['pending'] as OrderStatus[],
  color: 'teal',
  bgColor: 'bg-teal-50',
  borderColor: 'border-teal-200',
  textColor: 'text-teal-700',
  accentColor: 'bg-teal-400',
  icon: Clock,
  description: 'New refill requests'
}, {
  id: 'dispensed',
  label: 'Dispensed',
  statuses: ['dispensed'] as OrderStatus[],
  color: 'emerald',
  bgColor: 'bg-emerald-50',
  borderColor: 'border-emerald-200',
  textColor: 'text-emerald-700',
  accentColor: 'bg-emerald-500',
  icon: Package,
  description: 'Ready for delivery/pickup'
}, {
  id: 'out_for_delivery',
  label: 'Out for Delivery',
  statuses: ['out_for_delivery'] as OrderStatus[],
  color: 'sky',
  bgColor: 'bg-sky-50',
  borderColor: 'border-sky-200',
  textColor: 'text-sky-700',
  accentColor: 'bg-sky-500',
  icon: Navigation,
  description: 'In transit via OTO'
}, {
  id: 'delivered',
  label: 'Delivered',
  statuses: ['delivered'] as OrderStatus[],
  color: 'indigo',
  bgColor: 'bg-indigo-50',
  borderColor: 'border-indigo-200',
  textColor: 'text-indigo-700',
  accentColor: 'bg-indigo-500',
  icon: Truck,
  description: 'Handed off to customer'
}, {
  id: 'rejected',
  label: 'Cancelled',
  statuses: ['rejected', 'failed', 'timed_out'] as OrderStatus[],
  color: 'red',
  bgColor: 'bg-red-50',
  borderColor: 'border-red-200',
  textColor: 'text-red-700',
  accentColor: 'bg-red-500',
  icon: XCircle,
  description: 'Rejected or failed requests'
}];
export function Refills() {
  const {
    showToast
  } = useToast();
  const [orders, setOrders] = useState<UnifiedOrder[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(0);
  // Rejection Modal State
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  useEffect(() => {
    setOrders(mockUnifiedOrders);
  }, []);
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      setLastUpdated(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [autoRefresh]);
  const updateOrderStatus = (orderId: string, newStatus: OrderStatus) => {
    setOrders(prev => prev.map(order => order.id === orderId ? {
      ...order,
      status: newStatus
    } : order));
    const statusLabels: Record<string, string> = {
      dispensed: 'Approved & Dispensed',
      out_for_delivery: 'Sent to OTO Delivery',
      delivered: 'Marked as Delivered',
      rejected: 'Order Cancelled'
    };
    showToast({
      type: newStatus === 'rejected' ? 'error' : 'success',
      title: 'Order Updated',
      message: `Order has been ${statusLabels[newStatus] || newStatus}.`
    });
  };
  const handleApprove = (orderId: string) => {
    updateOrderStatus(orderId, 'dispensed');
  };
  const handleRejectClick = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsRejectionModalOpen(true);
  };
  const confirmRejection = () => {
    if (selectedOrderId && rejectionReason) {
      updateOrderStatus(selectedOrderId, 'rejected');
      setIsRejectionModalOpen(false);
      setSelectedOrderId(null);
      setRejectionReason('');
    }
  };
  const handleReadyForPickup = (orderId: string) => {
    showToast({
      type: 'success',
      title: 'Ready for Pickup',
      message: 'Customer has been notified that the order is ready for pickup.'
    });
    // In this flow, we might keep it in dispensed or move to a "ready" state,
    // but the user said "Delivered" is the final state.
    updateOrderStatus(orderId, 'delivered');
  };
  const handleReadyForDelivery = (orderId: string) => {
    // Simulate OTO request
    updateOrderStatus(orderId, 'out_for_delivery');
    // Simulate auto-completion after a delay (e.g., when OTO confirms delivery)
    setTimeout(() => {
      updateOrderStatus(orderId, 'delivered');
    }, 8000);
  };
  const getOrdersByStatus = (statuses: OrderStatus[]) => {
    return orders.filter(order => {
      if (!statuses.includes(order.status)) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!order.orderId.toLowerCase().includes(query) && !order.customer.name.toLowerCase().includes(query) && !order.branch.toLowerCase().includes(query) && !(order.erxReference && order.erxReference.toLowerCase().includes(query))) {
          return false;
        }
      }
      return true;
    });
  };
  const renderOrderCard = (order: UnifiedOrder, index: number) => {
    const liveSlaMinutes = Math.floor((Date.now() - new Date(order.receivedAt).getTime()) / 60000);
    const isSLABreach = liveSlaMinutes >= 15;
    const isSLAWarning = liveSlaMinutes >= 10 && liveSlaMinutes < 15;
    return <motion.div key={order.id} layout initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className={cn('rounded-lg border cursor-pointer transition-all shadow-sm hover:shadow-md overflow-hidden bg-white group relative border-l-4', order.status === 'rejected' || order.status === 'failed' || order.status === 'timed_out' ? 'border-l-red-500' : order.status === 'out_for_delivery' ? 'border-l-sky-500' : order.status === 'delivered' ? 'border-l-indigo-500' : 'border-l-teal-500', isSLABreach ? 'border-t-red-200 border-r-red-200 border-b-red-200 bg-red-50/50' : isSLAWarning ? 'border-t-amber-200 border-r-amber-200 border-b-amber-200 bg-amber-50/50' : 'border-t-slate-200 border-r-slate-200 border-b-slate-200')} data-id="element-901">
        <div className="p-4" data-id="element-902">
          {/* Header */}
          <div className="flex items-start justify-between mb-3" data-id="element-903">
            <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-904">
              <span className="text-2xl flex-shrink-0" data-id="element-905">💊</span>
              <div className="min-w-0 flex-1" data-id="element-906">
                <div className="font-bold text-slate-900 truncate" data-id="element-907">
                  {order.orderId}
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-908">
                  <Badge variant="neutral" size="sm" className="bg-slate-100 text-slate-600 border-slate-200 capitalize" data-id="element-909">
                    {order.status.replace('_', ' ')}
                  </Badge>
                </div>
              </div>
            </div>
            <div className={`flex-shrink-0 ml-2 text-xs font-bold px-2 py-1 rounded ${isSLABreach ? 'bg-red-100 text-red-700' : isSLAWarning ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-910">
              {liveSlaMinutes}m
            </div>
          </div>

          {/* eRx Reference */}
          {order.erxReference && <div className="mb-3 p-2 bg-teal-50 rounded border border-teal-200" data-id="element-911">
              <div className="text-[10px] text-teal-600 uppercase font-medium" data-id="element-912">
                eRx Reference
              </div>
              <div className="text-sm font-mono font-bold text-teal-900" data-id="element-913">
                {order.erxReference}
              </div>
            </div>}

          {/* Branch & Customer */}
          <div className="space-y-1.5 mb-3" data-id="element-914">
            <div className="flex items-center gap-2 text-xs text-slate-600 min-w-0" data-id="element-915">
              <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" data-id="element-916" />
              <span className="truncate" data-id="element-917">{order.branch}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600 min-w-0" data-id="element-918">
              <UserCheck className="w-3 h-3 text-slate-400 flex-shrink-0" data-id="element-919" />
              <span className="truncate font-medium" data-id="element-920">
                {order.customer.name}
              </span>
            </div>
          </div>

          {/* Delivery Method Badge */}
          <div className="mb-4" data-id="element-921">
            <Badge variant={!order.isPickup ? 'info' : 'neutral'} size="sm" data-id="element-922">
              {!order.isPickup ? '🚚 Home Delivery' : '🏪 Pickup'}
            </Badge>
          </div>

          {/* Actions Based on Status */}
          <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-2" data-id="element-923">
            {order.status === 'pending' && <>
                <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" onClick={() => handleApprove(order.id)} data-id="element-924">
                  <Check className="w-3.5 h-3.5" data-id="element-925" />
                  Approve
                </Button>
                <Button size="sm" variant="outline" className="flex-1 text-red-600 border-red-200 hover:bg-red-50 gap-1.5" onClick={() => handleRejectClick(order.id)} data-id="element-926">
                  <X className="w-3.5 h-3.5" data-id="element-927" />
                  Reject
                </Button>
              </>}

            {order.status === 'dispensed' && <>
                {order.isPickup ? <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" onClick={() => handleReadyForPickup(order.id)} data-id="element-928">
                    <CheckCircle2 className="w-3.5 h-3.5" data-id="element-929" />
                    Ready for Pickup
                  </Button> : <Button size="sm" className="w-full bg-sky-600 hover:bg-sky-700 text-white gap-1.5" onClick={() => handleReadyForDelivery(order.id)} data-id="element-930">
                    <Send className="w-3.5 h-3.5" data-id="element-931" />
                    Ready for Delivery (OTO)
                  </Button>}
              </>}

            {order.status === 'out_for_delivery' && <motion.div initial={{
            opacity: 0.5
          }} animate={{
            opacity: [0.5, 1, 0.5]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-full flex items-center justify-center gap-2 py-2.5 bg-sky-50 text-sky-700 rounded-lg border border-sky-100 text-[11px] font-bold uppercase tracking-tight" data-id="element-932">
                <div className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-pulse" data-id="element-933" />
                Waiting delivery confirmation
              </motion.div>}

            {(order.status === 'delivered' || order.status === 'rejected') && <div className="w-full flex items-center justify-center py-1 text-slate-400 text-xs italic" data-id="element-934">
                {order.status === 'delivered' ? 'Order Completed' : 'Order Cancelled'}
              </div>}
          </div>
        </div>
      </motion.div>;
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} className="h-[calc(100vh-8rem)] flex flex-col bg-slate-50 overflow-hidden -mx-4 sm:-mx-6 lg:-mx-8 -my-6" data-id="element-935">
      {/* Header */}
      <div className="border-b px-6 py-4 flex-shrink-0 bg-gradient-to-r from-teal-50 to-white border-teal-200" data-id="element-936">
        <div className="flex items-center justify-between mb-4" data-id="element-937">
          <div data-id="element-938">
            <div className="flex items-center gap-3" data-id="element-939">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-940">
                Dawaee Refills
              </h1>
              <div className="flex items-center gap-1.5 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium" data-id="element-941">
                <Pill className="w-4 h-4" data-id="element-942" />
                Refill Workflow
              </div>
              <Badge variant="info" size="sm" data-id="element-943">
                {orders.length} orders
              </Badge>
            </div>
            <div className="flex items-center gap-3 mt-1" data-id="element-944">
              <p className="text-slate-500" data-id="element-945">Manage incoming refill requests</p>
              {autoRefresh && <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-946">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" data-id="element-947" />
                  <span data-id="element-948">Live • Updated {lastUpdated}s ago</span>
                </div>}
            </div>
          </div>
          <div className="flex gap-3" data-id="element-949">
            <Button variant="outline" onClick={() => setAutoRefresh(!autoRefresh)} data-id="element-950">
              <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} data-id="element-951" />
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-3" data-id="element-952">
          <div className="relative flex-1" data-id="element-953">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-954" />
            <Input placeholder="Search by order ID, patient name, or eRx reference..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 border-teal-200 focus:ring-teal-500" data-id="element-955" />
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden min-h-0 bg-gradient-to-b from-teal-50/30 to-slate-50" data-id="element-956">
        <div className="flex gap-4 p-6 h-full min-w-max max-w-full" data-id="element-957">
          {refillColumns.map((column, colIndex) => {
          const columnOrders = getOrdersByStatus(column.statuses);
          const IconComponent = column.icon;
          return <motion.div key={column.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: colIndex * 0.1
          }} className="flex-shrink-0 w-80 flex flex-col" data-id="element-958">
                {/* Column Header */}
                <div className="mb-4" data-id="element-959">
                  <div className="flex items-center justify-between mb-2" data-id="element-960">
                    <div className="flex items-center gap-2" data-id="element-961">
                      <IconComponent className={`w-4 h-4 ${column.textColor}`} data-id="element-962" />
                      <h3 className={`text-sm font-bold ${column.textColor} uppercase tracking-wide`} data-id="element-963">
                        {column.label}
                      </h3>
                    </div>
                    <Badge variant="neutral" size="sm" data-id="element-964">
                      {columnOrders.length}
                    </Badge>
                  </div>
                  <div className={`h-1 rounded-full ${column.accentColor}`} data-id="element-965" />
                  <p className="text-xs text-slate-500 mt-2" data-id="element-966">
                    {column.description}
                  </p>
                </div>

                {/* Cards Container */}
                <div className="flex-1 space-y-3 pb-4 overflow-y-auto pr-2" data-id="element-967">
                  <AnimatePresence mode="popLayout" data-id="element-968">
                    {columnOrders.map((order, index) => renderOrderCard(order, index))}
                  </AnimatePresence>
                  {columnOrders.length === 0 && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-center py-8 text-slate-400" data-id="element-969">
                      <IconComponent className="w-8 h-8 mx-auto mb-2 opacity-50" data-id="element-970" />
                      <p className="text-sm" data-id="element-971">No orders</p>
                    </motion.div>}
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Rejection Modal */}
      <Modal isOpen={isRejectionModalOpen} onClose={() => setIsRejectionModalOpen(false)} title="Cancel Order" data-id="element-972">
        <div className="space-y-4" data-id="element-973">
          <p className="text-sm text-slate-500" data-id="element-974">
            Please select a reason for cancelling this refill request. This
            action cannot be undone.
          </p>

          <div className="space-y-2" data-id="element-975">
            <label className="text-sm font-medium text-slate-700" data-id="element-976">
              Cancellation Reason
            </label>
            <Select options={REJECTION_REASONS} value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Select a reason..." data-id="element-977" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6" data-id="element-978">
            <Button variant="outline" onClick={() => setIsRejectionModalOpen(false)} data-id="element-979">
              Go Back
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmRejection} disabled={!rejectionReason} data-id="element-980">
              Confirm Cancellation
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>;
}